import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { formatAdminDate } from 'utils/formatAdminDate';
import { formatDuration } from 'utils/formatDuration';
import {
  TD_INGEST_HISTORY_PAGE_SIZE,
  TD_INGEST_POLL_MS,
  TD_INGEST_POLL_TIMEOUT_MS,
} from 'constants/topdecked';
import {
  TdIngestRun,
  TdIngestRunStatus,
  useGetIngestHistoryQuery,
  useRunIngestMutation,
} from 'store/services/admin/adminTopDeckedApi';
import './TopDeckedIngestSection.css';

/**
 * "TopDecked Analytics Ingest" (ticket 1218427695140081): a read-only view of the
 * ingest job's run history plus a Run now trigger, mounted as a section on the
 * existing /admin/cronjobs page. Four summary tiles, a 30-row history table, two
 * conditional callouts, and a post-trigger poll. Loads once; no auto-refresh outside
 * the poll (decision 10).
 */

const STATUS_LABELS: Record<TdIngestRunStatus, string> = {
  success: 'Success',
  noop: 'No-op',
  refused: 'Refused',
  failed: 'Failed',
  running: 'Running',
};

// Plain hyphen: no em dashes in copy (ticket rule), and it matches formatDuration's null.
const DASH = '-';

/** Middle-truncate an opaque multipart ETag, keeping head and tail. */
export const truncateMiddle = (value: string, head = 8, tail = 6): string => {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

const StatusPill: React.FC<{ status: TdIngestRunStatus }> = ({ status }) => (
  <span className={`tdi-status tdi-status--${status}`}>{STATUS_LABELS[status] ?? status}</span>
);

const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : value.toLocaleString();

export const TopDeckedIngestSection: React.FC = () => {
  const [isPolling, setIsPolling] = useState(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runStartedAtRef = useRef<string | null>(null);

  const { data, isLoading, error, refetch } = useGetIngestHistoryQuery(undefined, {
    pollingInterval: isPolling ? TD_INGEST_POLL_MS : 0,
  });
  const [runIngest, { isLoading: isTriggering }] = useRunIngestMutation();

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Stop polling once the run we started has a finishedAt (its row is no longer
  // running). The 2 minute safety cutoff below stops it regardless of outcome.
  useEffect(() => {
    if (!isPolling || !data || !runStartedAtRef.current) return;
    const ours = data.runs.find((r) => r.startedAt === runStartedAtRef.current);
    if (ours && ours.finishedAt) {
      stopPolling();
      const label = STATUS_LABELS[ours.status] ?? ours.status;
      if (ours.status === 'refused' || ours.status === 'failed') {
        toast.warn(`Ingest run finished: ${label}`);
      } else {
        toast.success(`Ingest run finished: ${label}`);
      }
    }
  }, [isPolling, data, stopPolling]);

  const handleRunNow = async () => {
    try {
      const ack = await runIngest().unwrap();
      runStartedAtRef.current = ack.startedAt;
      setIsPolling(true);
      pollTimeoutRef.current = setTimeout(() => {
        setIsPolling(false);
        pollTimeoutRef.current = null;
      }, TD_INGEST_POLL_TIMEOUT_MS);
      toast.info('Ingest run started.');
    } catch (err: unknown) {
      const e = err as { status?: number; data?: { error?: string; message?: string } };
      if (e?.status === 409 || e?.data?.error === 'run_in_progress') {
        toast.info('A run is already in progress.');
        refetch();
      } else if (e?.status === 403) {
        toast.error('Only admins can trigger the ingest.');
      } else {
        toast.error(e?.data?.message || 'Failed to start the ingest run.');
      }
    }
  };

  const runs: TdIngestRun[] = data?.runs ?? [];
  const lastCompleted = runs.find((r) => r.finishedAt !== null) ?? null;
  const runInProgress = data?.runInProgress ?? false;
  const showRefusal = !!data?.lastRefusal;
  const showEnumWarnings = !!data?.lastSuccessful && data.lastSuccessful.enumWarningCount > 0;

  return (
    <section className="tdi-section" aria-labelledby="tdi-title">
      <div className="tdi-header">
        <div>
          <h2 id="tdi-title" className="tdi-title">
            TopDecked Analytics Ingest
          </h2>
          <p className="tdi-subtitle">Daily at 09:00 UTC. Downloads only when the export ETag changes.</p>
        </div>
        <div className="tdi-header-actions">
          {isPolling && (
            <span className="tdi-polling" role="status">
              Checking for the new run
            </span>
          )}
          <MagicButton
            label={isTriggering ? 'Starting...' : 'Run now'}
            color="purple"
            onPress={handleRunNow}
            isDisabled={isTriggering || isPolling || runInProgress || isLoading}
            isLoading={isTriggering}
          />
        </div>
      </div>

      {error ? (
        <div className="tdi-error" role="alert">
          Could not load the ingest history.
        </div>
      ) : null}

      <div className="tdi-tiles">
        <div className="tdi-tile">
          <span className="tdi-tile-value">{formatCount(data?.cardAnalyticsRowCount)}</span>
          <span className="tdi-tile-label">Rows in CardAnalytics</span>
        </div>
        <div className="tdi-tile">
          <span className="tdi-tile-value">
            {lastCompleted ? <StatusPill status={lastCompleted.status} /> : DASH}
          </span>
          <span className="tdi-tile-label">Last completed run</span>
        </div>
        <div className="tdi-tile">
          <span className="tdi-tile-value">{formatCount(data?.lastSuccessful?.droppedJunkNameCount)}</span>
          <span className="tdi-tile-label">Color-code names dropped (last success)</span>
        </div>
        <div className="tdi-tile">
          <span className="tdi-tile-value tdi-tile-value--date">
            {data?.sourceLastModified ? `${formatAdminDate(data.sourceLastModified)} UTC` : DASH}
          </span>
          <span className="tdi-tile-label">Export Last-Modified (diagnostic only)</span>
        </div>
      </div>

      <div className="tdi-table" role="table" aria-label="Ingest run history">
        <div className="tdi-table-header" role="row">
          <span role="columnheader">Started (UTC)</span>
          <span role="columnheader">Status</span>
          <span role="columnheader">ETag</span>
          <span role="columnheader">Records</span>
          <span role="columnheader">Junk dropped</span>
          <span role="columnheader">Duration</span>
        </div>
        {isLoading ? (
          <div className="tdi-row tdi-row--empty" role="row">
            <span role="cell">Loading...</span>
          </div>
        ) : runs.length === 0 ? (
          <div className="tdi-row tdi-row--empty" role="row">
            <span role="cell">No runs recorded yet.</span>
          </div>
        ) : (
          runs.slice(0, TD_INGEST_HISTORY_PAGE_SIZE).map((run) => (
            <div key={run.startedAt} className={`tdi-row tdi-row--${run.status}`} role="row">
              <span className="tdi-col-date" role="cell">{formatAdminDate(run.startedAt)}</span>
              <span role="cell"><StatusPill status={run.status} /></span>
              <span className="tdi-col-etag" role="cell" title={run.etag ?? undefined}>
                {run.etag ? truncateMiddle(run.etag) : DASH}
              </span>
              <span role="cell">{formatCount(run.recordCount)}</span>
              <span role="cell">{formatCount(run.droppedJunkNameCount)}</span>
              <span role="cell">{run.status === 'running' ? DASH : formatDuration(run.durationMs)}</span>
            </div>
          ))
        )}
      </div>

      {(showRefusal || showEnumWarnings) && (
        <div className="tdi-callouts">
          {showRefusal && data?.lastRefusal && (
            <div className="tdi-callout tdi-callout--refusal" role="note">
              <span className="tdi-callout-title">Sanity gate refused a load</span>
              <span>
                {formatAdminDate(data.lastRefusal.startedAt)} UTC: the export parsed{' '}
                {data.lastRefusal.recordCount.toLocaleString()} records against{' '}
                {data.lastRefusal.previousSuccessfulRecordCount.toLocaleString()} on the previous
                successful run.
              </span>
              {data.lastRefusal.errorMessage ? (
                <span className="tdi-callout-detail">{data.lastRefusal.errorMessage}</span>
              ) : null}
              <span>Existing rows were left untouched.</span>
            </div>
          )}
          {showEnumWarnings && data?.lastSuccessful && (
            <div className="tdi-callout tdi-callout--enum" role="note">
              <span className="tdi-callout-title">Enum warnings on the last successful run</span>
              <span>
                {data.lastSuccessful.enumWarningCount.toLocaleString()} unrecognized enum{' '}
                {data.lastSuccessful.enumWarningCount === 1 ? 'value' : 'values'}. The affected fields
                were nulled and no record was dropped.
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default TopDeckedIngestSection;
